// SessionExpiredModal — re-auth overlay when the session expires mid-task.
//
// Visual + copy from
//   design/UI : UX v1.0/for-engineering-auth/specs/screen-strings.md § 8.
//
// Behavior:
//  - Renders ONLY when useAuth().status.kind === 'session-expired'.
//  - Modal is must-decide: closeOnEsc=false, closeOnScrimClick=false,
//    showCloseButton=false — user must either reauth or sign out.
//  - On successful reauth(), AuthFlowApi flips to 'authenticated' and the
//    modal unmounts; the queued request that triggered the 401 is replayed
//    by the API client (per useAuthFlow §reauth-replay docs).
//  - The "previousEmail" surfaced on the session-expired state is the
//    account label shown in the modal — no password manager surprises.

import { useEffect, useState, type FormEvent } from 'react';
import { Alert, Button, Modal, TextInput } from '@gc-coach/design-system';
import { useAuth } from './AuthContext.js';
import { useSession } from './SessionContext.js';
import { useI18n } from '../i18n/index.js';
import styles from './SessionExpiredModal.module.css';

export function SessionExpiredModal() {
  const { t, tt } = useI18n();
  const { status, error, reauth, signOut, clearError } = useAuth();
  const { session } = useSession();

  const [password, setPassword] = useState('');
  // Idle clock: spec shows "{N} MIN INACTIVE".
  //
  // PREVIOUS BUG: mountedAt was captured at component mount, which —
  // because SessionExpiredModal is mounted at app boot inside the global
  // provider tree — meant the value was the user's app-load time. Hours
  // of authenticated usage would inflate the "MIN INACTIVE" count by the
  // total app uptime, not the actual idle window.
  //
  // FIX: anchor the clock to the moment status FIRST becomes
  // 'session-expired'. The useAuthFlow idle timer fires
  // session-expired after IDLE_TIMEOUT_MS (currently 20 minutes); we
  // display that threshold plus however many additional minutes have
  // passed since the modal popped. If session-expired was triggered by
  // a 401 rather than the idle timer, the 20-minute baseline is still
  // a reasonable approximation of "you walked away."
  //
  // IDLE_THRESHOLD_MIN must stay in sync with useAuthFlow.IDLE_TIMEOUT_MS.
  // If that constant changes, change here too.
  const IDLE_THRESHOLD_MIN = 20;
  const [sessionExpiredAt, setSessionExpiredAt] = useState<number | null>(null);
  useEffect(() => {
    if (status.kind === 'session-expired') {
      if (sessionExpiredAt === null) {
        setSessionExpiredAt(Date.now());
      }
    } else {
      // Reset so the next session-expired event starts a fresh clock.
      setSessionExpiredAt(null);
    }
  }, [status.kind, sessionExpiredAt]);

  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const inactiveMinutes = sessionExpiredAt
    ? IDLE_THRESHOLD_MIN +
      Math.max(0, Math.round((now - sessionExpiredAt) / 60_000))
    : IDLE_THRESHOLD_MIN;

  if (status.kind !== 'session-expired') return null;

  const submitting = status.kind !== 'session-expired'; // reauth() triggers state change away
  // Prefer the email from the cached /auth/me session — that's the
  // authoritative identity (decoded from id_token claims). Fall back to
  // status.previousEmail (set by useAuthFlow at sign-in time) only if
  // session is unavailable. Defensive against any edge case where
  // previousEmail might carry a Cognito sub instead of an email.
  const email = session?.email || status.previousEmail;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password || submitting) return;
    try {
      await reauth(password);
      // Success → useAuthFlow transitions to 'authenticated' → this component unmounts.
    } catch {
      // error surfaced via useAuth().error — Alert below picks it up.
      setPassword('');
    }
  };

  function mapErrorToCopy(): string {
    if (!error) return '';
    switch (error.code) {
      case 'INVALID_CREDENTIALS':
        return t('sessionExpired.errorWrongPassword');
      case 'RATE_LIMITED':
        return t('sessionExpired.errorRateLimited');
      default:
        return t('sessionExpired.errorGeneric');
    }
  }

  return (
    <Modal
      open
      onClose={() => undefined}
      closeOnEsc={false}
      closeOnScrimClick={false}
      showCloseButton={false}
      blurScrim
      width={480}
    >
      <header className={styles.header}>
        <span className={styles.badge}>{t('sessionExpired.badge')}</span>
        <span className={styles.caption}>
          {tt('sessionExpired.captionTemplate', { minutes: inactiveMinutes })}
        </span>
      </header>

      <h2 className={styles.headline}>{t('sessionExpired.headline')}</h2>
      <p className={styles.body}>{t('sessionExpired.body')}</p>

      <form onSubmit={onSubmit} className={styles.form} noValidate>
        {error && (
          <Alert severity="danger" onDismiss={clearError}>
            {mapErrorToCopy()}
          </Alert>
        )}

        <div className={styles.accountRow}>
          <span className={styles.fieldLabel}>{t('sessionExpired.accountLabel')}</span>
          <span className={styles.accountValue}>{email}</span>
        </div>

        <TextInput
          label={t('sessionExpired.passwordLabel')}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
          disabled={submitting}
          revealable
          required
          autoFocus
        />

        <Button type="submit" kind="brand" fullWidth loading={submitting}>
          {t('sessionExpired.submitButton')}
        </Button>

        <footer className={styles.footer}>
          <button
            type="button"
            onClick={() => void signOut()}
            className={styles.signOutLink}
            disabled={submitting}
          >
            {t('sessionExpired.signOutLink')}
          </button>
          <span className={styles.saveCaption}>{t('sessionExpired.saveCaption')}</span>
        </footer>
      </form>
    </Modal>
  );
}
